import {
  Button,
  Flex,
  Group,
  Progress,
  Stack,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { INFLEARN_URL } from '../../../../../constants/urls';
import useMediaQuery from '../../../../../styles/useMediaQuery';
import { CourseCardMenu } from './CourseCardMenu';
import { FeedbackButtons } from './FeedbackButtons';
import type { CourseCardData } from './courseCardDataTransformer';

type Props = {
  progress: number;
  completeCount: number;
  hasCertificate: boolean;
  isArchived: boolean;
  isArchivable: boolean;
  isCancelable: boolean;
  course: CourseCardData['course'];
  voucherId: number;
  isReviewable: boolean;
  review: CourseCardData['review'];
};

export const ChallengeCourseCardMetaInfo = ({
  progress,
  completeCount,
  hasCertificate,
  isArchived,
  isArchivable,
  isCancelable,
  course,
  voucherId,
  isReviewable,
  review,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();

  const { lectureCount } = course;

  const hasSurveyAnswer = course.challengeCourse?.survey?.hasSurveyAnswer;

  return (
    <>
      <Progress
        aria-label={t('진도율')}
        value={progress}
        size={isMobile ? 'sm' : 'md'}
        color="infgreen"
      />
      <Flex w="100%">
        <Stack
          spacing={0}
          w="100%"
          css={{
            'span, p': {
              fontSize: 14,
            },
            overflow: 'hidden',
            [desktopFirstMediaQuery.mobile]: {
              'span, p': {
                fontSize: 12,
              },
            },
          }}
        >
          <Flex gap={2}>
            <Text color="dimmed" component="span" weight={600} c="gray.7">
              {completeCount}
            </Text>
            <Text color="dimmed" component="span" c="gray.7">
              /{' '}
              {t('{{count}}강', {
                count: lectureCount,
                context: 'courseCard',
              })}
            </Text>
            <Text color="dimmed" component="span" c="gray.7">
              ({progress}%)
            </Text>
          </Flex>
          <Text
            color="gray.6"
            css={{
              overflow: 'auto',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {course.challengeCourse && (
              <>
                {dayjs(course.challengeCourse.startedAtUTC).format('YY. MM. DD. HH:mm')} {'부터'}{' '}
                {dayjs(course.challengeCourse.endedAtUTC).format('YY. MM. DD. HH:mm')}
              </>
            )}
          </Text>
        </Stack>
        <CourseCardMenu
          hasCertificate={hasCertificate}
          isOfflineCourse={course.isOfflineCourse}
          isArchived={isArchived}
          isArchivable={isArchivable}
          isCancelable={isCancelable}
          courseId={course.courseId}
          voucherId={voucherId}
          isPartners={false}
        />
      </Flex>

      <Group spacing={8}>
        <Button
          variant={hasSurveyAnswer ? 'light' : 'default'}
          size="xs"
          color={hasSurveyAnswer ? 'gray' : undefined}
          onClick={() => {
            window.location.href = INFLEARN_URL.CHALLENGE_SURVEY(course.courseId);
          }}
        >
          {hasSurveyAnswer ? t('설문 수정') : t('설문 작성')}
        </Button>

        <FeedbackButtons
          isReviewable={isReviewable}
          review={review}
          voucherId={voucherId}
          course={course}
        />
      </Group>
    </>
  );
};
